import React, { useState, useEffect } from 'react';
import { useTranslate, useNotify } from 'react-admin';
import { recommendationService, Recommendation } from '../../services/recommendationService';
import { aiRecommendationService, type AiRecommendation } from '../../services/aiRecommendationService';
import { useCourseEnrollment } from '../../hooks/useCourseEnrollment';
import { displayEnrollmentMode } from '../../utils/enrollmentMode';
import MaterialSymbol from '../common/MaterialSymbol';

interface RecommendedCoursesWidgetProps {
    limit?: number;
}

/**
 * "Empfohlen für Sie" — Empfehlungs-Karten im neuen Design (.reco/.rcard,
 * Hero einfarbig in var(--oa-primary), kein Gradient — Kunden-Feedback).
 *
 * Re-Skin von components/Recommendations/RecommendedCourses mit identischem
 * Verhalten (Issue #318): Datenabruf via recommendationService, Einschreibung
 * inline über useCourseEnrollment — kein "Alle anzeigen", keine Navigation
 * in die Kursverwaltung (/courses/*).
 */
const RecommendedCoursesWidget: React.FC<RecommendedCoursesWidgetProps> = ({ limit = 4 }) => {
    const translate = useTranslate();
    const raNotify = useNotify();

    const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    // #517: AI picks with a reason replace the rule-based list once they exist;
    // the rule-based list stays as the immediate fallback (feature off, LLM down).
    const [aiRecommendations, setAiRecommendations] = useState<AiRecommendation[] | null>(null);
    const [aiPending, setAiPending] = useState(false);

    const { enroll } = useCourseEnrollment({
        notify: (message, severity) => raNotify(message, { type: severity }),
    });

    useEffect(() => {
        const loadRecommendations = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await recommendationService.getRecommendations(limit);
                setRecommendations(data);
            } catch (err) {
                console.error('Failed to load recommendations:', err);
                setError(err instanceof Error ? err.message : 'Failed to load recommendations');
            } finally {
                setLoading(false);
            }
        };
        loadRecommendations();
    }, [limit]);

    useEffect(() => {
        let cancelled = false;
        const loadAi = async () => {
            try {
                const stored = await aiRecommendationService.list();
                if (cancelled) return;
                if (stored.count > 0) {
                    setAiRecommendations(stored.recommendations);
                    return;
                }
                setAiPending(true);
                const fresh = await aiRecommendationService.refresh();
                if (!cancelled && fresh.count > 0) setAiRecommendations(fresh.recommendations);
            } catch {
                // 403 (feature off) or 502 (LLM) → keep the rule-based list silently
            } finally {
                if (!cancelled) setAiPending(false);
            }
        };
        void loadAi();
        return () => { cancelled = true; };
    }, []);

    const aiByCourse = new Map((aiRecommendations ?? []).map((r) => [r.course.id, r]));
    const shown: Recommendation[] = aiRecommendations && aiRecommendations.length > 0
        ? aiRecommendations.slice(0, limit).map((r) => ({ course: r.course, score: r.score, reasons: [r.source], reasonLabels: [r.reason], enrollmentMode: r.enrollmentMode, isLicensedReference: r.isLicensedReference }))
        : recommendations;

    const handleEnroll = (courseId: number) => {
        const ai = aiByCourse.get(courseId);
        if (ai) void aiRecommendationService.click(ai.id).catch(() => undefined);
        const rec = shown.find(r => r.course.id === courseId);
        if (rec) {
            enroll({
                id: rec.course.id,
                title: rec.course.title,
                enrollmentMode: rec.enrollmentMode,
                isLicensedReference: rec.isLicensedReference,
            });
        }
    };

    if (loading) {
        return (
            <div className="panel">
                <div className="skel" />
                <div className="skel" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="panel">
                <p className="empty-note">{error}</p>
            </div>
        );
    }

    if (shown.length === 0) {
        return null;
    }

    return (
        <div>
            <div className="sec-head">
                <h2 className="sec-title">
                    <span className="ico green"><MaterialSymbol name="star" size={19} /></span>
                    {translate('recommendations.title', { _: 'Empfohlen für Sie' })}
                    {aiByCourse.size > 0 && <span className="chip-out green" data-testid="reco-ai-badge" title={translate('recommendations.aiHint', { _: 'Von der KI ausgewählt' })}>{translate('recommendations.aiBadge', { _: 'KI' })}</span>}
                </h2>
                {aiPending && <span className="empty-note" data-testid="reco-ai-pending">{translate('recommendations.aiPending', { _: 'Die KI stellt gerade Empfehlungen zusammen …' })}</span>}
            </div>
            <div className="reco">
                {shown.map((rec) => {
                    const { course } = rec;
                    const ai = aiByCourse.get(course.id);
                    const primaryReason = ai ? ai.reason : recommendationService.getPrimaryReason(rec);
                    const hasThumb = Boolean(course.thumbnailUrl);

                    return (
                        <div className="rcard" key={course.id}>
                            <div
                                className={`hero${hasThumb ? ' thumb' : ''}`}
                                style={hasThumb ? { backgroundImage: `url(${course.thumbnailUrl})` } : undefined}
                            >
                                {!hasThumb && <MaterialSymbol name="school" size={52} />}
                                <span className="spark" title={rec.reasonLabels?.join(', ')}>
                                    <MaterialSymbol name="star" size={16} />
                                </span>
                            </div>
                            <div className="body">
                                <div className="tags">
                                    <span className="chip-out green">
                                        {recommendationService.getDifficultyLabel(course.difficulty)}
                                    </span>
                                    {course.catalog && <span className="chip-out">{course.catalog.name}</span>}
                                    {course.estimatedDuration ? (
                                        <span className="chip-out">
                                            {recommendationService.formatDuration(course.estimatedDuration)}
                                        </span>
                                    ) : null}
                                </div>
                                <h3>{course.title}</h3>
                                {primaryReason && (
                                    <div className="ideal">
                                        <MaterialSymbol name="star" size={16} />
                                        {primaryReason}
                                    </div>
                                )}
                                {rec.enrollmentMode === 'none' ? (
                                    <div className="contact">
                                        {translate('recommendations.contactAdmin', { _: 'Kontaktieren Sie Ihre Ansprechpartner' })}
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        className="btn-primary enroll"
                                        onClick={() => handleEnroll(course.id)}
                                    >
                                        {displayEnrollmentMode(rec.enrollmentMode) === 'request'
                                            ? translate('recommendations.requestEnroll', { _: 'Einschreibung anfragen' })
                                            : translate('recommendations.enroll', { _: 'Einschreiben' })}
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default RecommendedCoursesWidget;
